import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { MessageSquare, ChevronDown, ChevronUp, Video, ExternalLink } from "lucide-react";
import { API } from "./shared/utils";
import axios from "axios";

const FeedbackReceivedWidget = ({ currentUser, challenges }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (currentUser?.id) fetchFeedback();
  }, [currentUser?.id]);

  const fetchFeedback = async () => {
    try {
      const res = await axios.get(`${API}/members/${currentUser.id}/feedback-received`);
      setFeedbacks(res.data);
    } catch (err) {
      console.error("Error fetching feedback:", err);
    }
  };

  const displayed = expanded ? feedbacks : feedbacks.slice(0, 3);

  return (
    <Card className="bg-gradient-to-br from-slate-800/90 to-purple-900/30 border-purple-500/30 shadow-xl backdrop-blur-sm" data-testid="feedback-received-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-white text-base">
              <MessageSquare className="w-5 h-5 text-purple-400" />
              Feedback Received
            </CardTitle>
            <CardDescription className="text-slate-400">
              {feedbacks.length > 0 
                ? `${feedbacks.length} feedback${feedbacks.length !== 1 ? 's' : ''} from your peers`
                : 'Feedback from your peers will appear here'}
            </CardDescription>
          </div>
          {feedbacks.length > 0 && (
            <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
              {feedbacks.length}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {feedbacks.length === 0 ? (
          <div className="text-center py-4 bg-slate-900/30 rounded-lg border border-slate-700/50">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-600" />
            <p className="text-slate-400 text-sm">No feedback received yet</p>
            <p className="text-slate-500 text-xs mt-1">When peers review your submissions, their SPARK feedback will show here</p>
          </div>
        ) : (
          <>
        {displayed.map((fb) => {
          const challenge = challenges?.find(c => c.id === fb.challenge_id);
          const hasSpark = fb.spark_improvement || fb.spark_actionable;
          const videoId = fb.content_url?.includes('youtube.com/watch?v=')
            ? fb.content_url.split('v=')[1]?.split('&')[0]
            : fb.content_url?.includes('youtu.be/')
            ? fb.content_url.split('youtu.be/')[1]?.split('?')[0]
            : null;

          return (
            <div key={fb.id} className="p-3 rounded-lg bg-slate-900/50 border border-slate-700/50">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xs">
                    {fb.giver_name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{fb.giver_name}</p>
                    <p className="text-slate-500 text-xs">{challenge?.title?.substring(0, 30) || 'Challenge'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {fb.admin_approved && <Badge className="bg-green-500/20 text-green-300 text-xs px-1.5 py-0">Approved</Badge>}
                  {fb.feedback_mode === 'video' && <Badge className="bg-cyan-500/20 text-cyan-300 text-xs px-1.5 py-0">Video</Badge>}
                </div>
              </div>

              {/* SPARK feedback display */}
              {hasSpark ? (
                <div className="space-y-1.5 text-sm">
                  {fb.spark_specific && (
                    <div className="flex gap-2">
                      <span className="w-4 h-4 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 text-[10px] font-bold flex-shrink-0 mt-0.5">S</span>
                      <p className="text-slate-300">{fb.spark_specific}</p>
                    </div>
                  )}
                  {fb.spark_improvement && (
                    <div className="flex gap-2">
                      <span className="w-4 h-4 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 text-[10px] font-bold flex-shrink-0 mt-0.5">P</span>
                      <p className="text-slate-300">{fb.spark_improvement}</p>
                    </div>
                  )}
                  {fb.spark_actionable && (
                    <div className="flex gap-2">
                      <span className="w-4 h-4 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 text-[10px] font-bold flex-shrink-0 mt-0.5">A</span>
                      <p className="text-slate-300">{fb.spark_actionable}</p>
                    </div>
                  )}
                  {fb.spark_reflection && (
                    <div className="flex gap-2">
                      <span className="w-4 h-4 rounded-full bg-pink-500/20 flex items-center justify-center text-pink-400 text-[10px] font-bold flex-shrink-0 mt-0.5">R</span>
                      <p className="text-slate-300">{fb.spark_reflection}</p>
                    </div>
                  )}
                  {fb.spark_continue && (
                    <div className="flex gap-2">
                      <span className="w-4 h-4 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-[10px] font-bold flex-shrink-0 mt-0.5">K</span>
                      <p className="text-slate-300">{fb.spark_continue}</p>
                    </div>
                  )}
                </div>
              ) : fb.feedback_text ? (
                <p className="text-slate-300 text-sm whitespace-pre-wrap">{fb.feedback_text}</p>
              ) : null}

              {/* Video link if present */}
              {fb.content_url && fb.content_url !== 'None' && fb.content_url.trim() !== '' && (
                <a href={fb.content_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 mt-2">
                  <ExternalLink className="w-3 h-3" />
                  View feedback video
                </a>
              )}

              <p className="text-slate-600 text-xs mt-2">
                {new Date(fb.submission_time).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
          );
        })}

        {feedbacks.length > 3 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="w-full text-slate-400 hover:text-white"
          >
            {expanded ? (
              <><ChevronUp className="w-4 h-4 mr-1" /> Show Less</>
            ) : (
              <><ChevronDown className="w-4 h-4 mr-1" /> Show All {feedbacks.length}</>
            )}
          </Button>
        )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FeedbackReceivedWidget;
